import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { receiptsAPI, billsAPI } from '../utils/api';
import { useSettings } from '../hooks/useSettings';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

export default function NewReceipt() {
  const navigate = useNavigate();
  const location = useLocation();
  const { settings } = useSettings();
  const cur = settings.currency || 'BDT';

  const prefill = location.state || {};

  const [form, setForm] = useState({
    bill_id: prefill.billId || '',
    client_name: prefill.clientName || '',
    client_address: prefill.clientAddress || '',
    payment_date: format(new Date(), 'yyyy-MM-dd'),
    amount: '',
    payment_method: 'cash',
    reference_number: '',
    notes: '',
  });
  const [bills, setBills] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    billsAPI.getAll({ limit: 100 })
      .then(res => setBills(res.data.bills))
      .catch(() => {});
  }, []);

  // Calculate due amount when coming from bill detail
  useEffect(() => {
    if (!prefill.billId) return;
    receiptsAPI.getAll({ limit: 100 })
      .then(res => {
        const paid = res.data.receipts
          .filter(r => String(r.bill_id) === String(prefill.billId))
          .reduce((s, r) => s + parseFloat(r.amount), 0);
        billsAPI.getOne(prefill.billId).then(b => {
          const due = parseFloat(b.data.total) - paid;
          if (due > 0) setForm(f => ({ ...f, amount: due.toString() }));
        });
      })
      .catch(() => {});
  }, [prefill.billId]);

  // When bill is selected from dropdown, auto-fill client and calculate due
  const handleBillChange = async (e) => {
    const billId = e.target.value;
    if (!billId) {
      setForm(f => ({ ...f, bill_id: '' }));
      return;
    }
    const selected = bills.find(b => String(b.id) === String(billId));
    if (selected) {
      setForm(f => ({
        ...f,
        bill_id: billId,
        client_name: selected.client_name || f.client_name,
        client_address: selected.client_address || f.client_address,
      }));

      // Calculate remaining due
      try {
        const res = await receiptsAPI.getAll({ limit: 100 });
        const paid = res.data.receipts
          .filter(r => String(r.bill_id) === String(billId))
          .reduce((s, r) => s + parseFloat(r.amount), 0);
        const due = parseFloat(selected.total) - paid;
        if (due > 0) setForm(f => ({ ...f, amount: due.toString() }));
      } catch {}
    }
  };

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_name.trim()) return toast.error('Client name required');
    if (!form.amount || parseFloat(form.amount) <= 0) return toast.error('Valid amount required');

    setSaving(true);
    try {
      const res = await receiptsAPI.create(form);
      toast.success('Receipt created!');
      navigate(`/receipts/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // Find selected bill details for due summary
  const selectedBill = bills.find(b => String(b.id) === String(form.bill_id));

  return (
    <div style={{ maxWidth: 700 }}>
      <Link to="/receipts" className="back-link">← Back to Receipts</Link>
      <h1 className="page-title">New Receipt</h1>

      <form onSubmit={handleSubmit}>

        {/* Link to Bill */}
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-header"><span className="card-title">Link to Bill (Optional)</span></div>
          <div className="card-body">
            <div className="form-group">
              <label className="form-label">Select Bill</label>
              <select
                className="form-control"
                value={form.bill_id}
                onChange={handleBillChange}
              >
                <option value="">-- No linked bill --</option>
                {bills.filter(b => b.status === 'unpaid' || b.status === 'partial').map(b => (
  <option key={b.id} value={b.id}>
    {b.bill_number} — {b.client_name} ({settings.currency || 'BDT'} {parseFloat(b.total).toLocaleString()}) [{b.status}]
  </option>
))}
              </select>
              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
                Linking a bill will auto-update its payment status.
              </div>
            </div>

            {/* Bill Due Summary */}
            {selectedBill && (
              <div style={{
                background: '#f8fafc', borderRadius: 10, padding: '12px 16px',
                border: '1.5px solid #e2e8f0', marginTop: 4
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 13 }}>
                  <span style={{ color: '#64748b' }}>Bill Total</span>
                  <span style={{ fontWeight: 600 }}>{cur} {parseFloat(selectedBill.total).toLocaleString()}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 13 }}>
                  <span style={{ color: '#64748b' }}>Status</span>
                  <span className={`badge badge-${selectedBill.status}`}>{selectedBill.status}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, fontWeight: 700, borderTop: '1px solid #e2e8f0', paddingTop: 8 }}>
                  <span>Amount Pre-filled</span>
                  <span style={{ color: '#2563eb' }}>{cur} {parseFloat(form.amount || 0).toLocaleString()}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Client Info */}
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-header"><span className="card-title">Client Information</span></div>
          <div className="card-body">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Client Name *</label>
                <input
                  className="form-control"
                  value={form.client_name}
                  onChange={e => setField('client_name', e.target.value)}
                  placeholder="Client or company name"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Client Address</label>
                <input
                  className="form-control"
                  value={form.client_address}
                  onChange={e => setField('client_address', e.target.value)}
                  placeholder="City, Country"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Payment Details */}
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-header"><span className="card-title">Payment Details</span></div>
          <div className="card-body">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Payment Date *</label>
                <input
                  type="date"
                  className="form-control"
                  value={form.payment_date}
                  onChange={e => setField('payment_date', e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Amount ({cur}) *</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="form-control"
                  value={form.amount}
                  onChange={e => setField('amount', e.target.value)}
                  placeholder="0.00"
                  required
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Payment Method</label>
                <select
                  className="form-control"
                  value={form.payment_method}
                  onChange={e => setField('payment_method', e.target.value)}
                >
                  <option value="cash">Cash</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="cheque">Cheque</option>
                  <option value="online">Online</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Reference / Transaction No.</label>
                <input
                  className="form-control"
                  value={form.reference_number}
                  onChange={e => setField('reference_number', e.target.value)}
                  placeholder="Optional reference"
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Notes / Description</label>
              <textarea
                className="form-control"
                rows={2}
                value={form.notes}
                onChange={e => setField('notes', e.target.value)}
                placeholder="e.g. Software Monthly Subscription Fee (October)"
              />
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <Link to="/receipts" className="btn btn-outline">Cancel</Link>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving...' : 'Create Receipt'}
          </button>
        </div>

      </form>
    </div>
  );
}